import baseApi from ".";
import api from "api";
import createQueryFn from "../createQueryFn";
import {
  FilterTimeEntriesRequest,
  FilterTimeEntriesResponse,
} from "api/absence/filterTimeEntries";
import {
  FilterAllTimeEntriesRequest,
  FilterAllTimeEntriesResponse,
} from "api/absence/filterAllTimeEntries";
import {
  GetAllTimeEntriesRequest,
  GetAllTimeEntriesResponse,
} from "api/absence/getAllTimeEntries";
import {
  SetTimeEntryApprovalRequest,
  SetTimeEntryApprovalResponse,
} from "api/absence/setTimeEntryApproval";
import {
  SetTimeEntryProcessedRequest,
  SetTimeEntryProcessedResponse,
} from "api/absence/setTimeEntryProcessed";
import { TimeEntry } from "models/TimeEntry";
import { SetBatchStateRequest, SetBatchStateResponse } from "api/absence/batchUpdateState";
import { SetBatchProcessedRequest, SetBatchProcessedResponse } from "api/absence/batchUpdateProcessed";
import { FilterAllTimeEntriesPublicRequest, FilterAllTimeEntriesPublicResponse } from "api/absence/filterAllTimeEntriesPublic";

const absenceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllTimeEntries: builder.query<GetAllTimeEntriesResponse, GetAllTimeEntriesRequest>({
      queryFn: createQueryFn(api.absence.getAllTimeEntries),
      providesTags: [{ type: "TimeEntry", id: "LIST" }],
    }),

    filterTimeEntries: builder.infiniteQuery<TimeEntry[], FilterTimeEntriesRequest, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1,
      },
      query: ({ queryArg, pageParam }) => {
        return {
          url: "absence/filter",
          method: "POST",
          body: {
            ...queryArg,
            page: pageParam,
            pageSize: 50,
          },
        };
      },
      transformResponse: (response: FilterTimeEntriesResponse) => response?.results || [],
      providesTags: () => [{ type: "TimeEntry", id: "LIST" }],
    }),

    filterAllTimeEntries: builder.query<
      FilterAllTimeEntriesResponse,
      FilterAllTimeEntriesRequest
    >({
      queryFn: createQueryFn(api.absence.filterAllTimeEntries),
      providesTags: [{ type: "TimeEntry", id: "LIST" }],
    }),

    filterAllTimeEntriesPublic: builder.query<
      FilterAllTimeEntriesPublicResponse,
      FilterAllTimeEntriesPublicRequest
    >({
      queryFn: createQueryFn(api.absence.filterAllTimeEntriesPublic),
      providesTags: [{ type: "TimeEntry", id: "LIST" }],
    }),

    setTimeEntryApproval: builder.mutation<
      SetTimeEntryApprovalResponse,
      SetTimeEntryApprovalRequest
    >({
      queryFn: createQueryFn(api.absence.setTimeEntryApproval),
      invalidatesTags: () => [
        { type: "TimeEntry", id: "LIST" },
      ],
    }),

    setTimeEntryProcessed: builder.mutation<
      SetTimeEntryProcessedResponse,
      SetTimeEntryProcessedRequest
    >({
      queryFn: createQueryFn(api.absence.setTimeEntryProcessed),
      invalidatesTags: () => [
        { type: "TimeEntry", id: "LIST" },
      ],
    }),

    batchUpdateState: builder.mutation<
      SetBatchStateResponse,
      SetBatchStateRequest
    >({
      queryFn: createQueryFn(api.absence.batchUpdateState),
      invalidatesTags: () => [
        { type: "TimeEntry", id: "LIST" },
      ],
    }),

    batchUpdateProcessed: builder.mutation<
      SetBatchProcessedResponse,
      SetBatchProcessedRequest
    >({
      queryFn: createQueryFn(api.absence.batchUpdateProcessed),
      invalidatesTags: () => [
        { type: "TimeEntry", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetAllTimeEntriesQuery,
  useFilterTimeEntriesInfiniteQuery,
  useFilterAllTimeEntriesQuery,
  useFilterAllTimeEntriesPublicQuery,
  useSetTimeEntryApprovalMutation,
  useSetTimeEntryProcessedMutation,
  useBatchUpdateStateMutation,
  useBatchUpdateProcessedMutation,
} = absenceApi;

export default absenceApi;
